import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  getDisabledSaveSearchActionButtonTooltip,
  shouldSaveSearchActionButtonDisabled,
} from '~/components/SearchV2/SearchV2.utils';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { Button } from '~/eds/src/components/Button/Button';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { useIntl } from 'react-intl';
import { buildQuery } from '~/redux/api/methods/search';
import { api } from '~/redux/api/createApi';
import * as selectors from '~/redux/selectors';
import { ERROR, SUCCESS } from '~/types/toast.types';
import { useModal } from '~/ui/hooks/modal';

import SaveSearchForm from '../SaveSearchForm/SaveSearchForm';

const SaveSearch = ({ queryProvided }) => {
  const intl = useIntl();
  const query = useSelector(selectors.selectQuery);
  const unsupportedSavedSearchQuery = useSelector(
    selectors.selectUnsupportedSavedSearchQuery,
  );

  const hasSearchQuery =
    unsupportedSavedSearchQuery?.length ||
    queryProvided?.length ||
    query?.booleanQuery?.length > 0 ||
    query?.filters?.length > 0;

  const [formData, setFormData] = useState();

  const [saveSearch, saveSearchResult] = api.endpoints.saveSearch.useMutation();
  const { isError: saveSearchError, isSuccess: saveSearchSuccess } =
    saveSearchResult;

  const resetSaveSearchInputs = () => {
    setFormData(undefined);
  };

  useEffect(() => {
    if (saveSearchError) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'SaveSearch.saveFailedMessage',
          defaultMessage: 'Save search failed. Please try again.',
          description: 'Error message when save search fails',
        }),
      );
    }
  }, [saveSearchError]);

  useEffect(() => {
    if (saveSearchSuccess) {
      resetSaveSearchInputs();
      showToast(
        SUCCESS,
        intl.formatMessage({
          id: 'SaveSearch.saveSuccessMessage',
          defaultMessage: 'Search has been successfully saved.',
          description: 'Success message when search is saved',
        }),
      );
    }
  }, [saveSearchSuccess]);

  const getSaveSearchIconTooltip = () => {
    if (!query && !queryProvided && !unsupportedSavedSearchQuery?.length) {
      return intl.formatMessage({
        id: 'SaveSearch.noSearchTooltip',
        defaultMessage:
          'Enter text in the search bar and/or apply filters to perform a search before saving',
        description: 'Tooltip when no search query is available',
      });
    } else {
      return intl.formatMessage({
        id: 'SaveSearch.saveSearchTooltip',
        defaultMessage: 'Save Search',
        description: 'Tooltip for save search button',
      });
    }
  };

  const legacyQuery = unsupportedSavedSearchQuery || queryProvided;

  const noQueryProvided = !(
    unsupportedSavedSearchQuery ||
    queryProvided ||
    query
  );
  const [saveSearchModal, showSaveSearchModal] = useModal({
    onHide: () => resetSaveSearchInputs(),
    onCancel: () => resetSaveSearchInputs(),
    actionButton: {
      disabled:
        shouldSaveSearchActionButtonDisabled(formData) || noQueryProvided,
      promise: () => {
        const queryToSave = legacyQuery ?? buildQuery(query);
        const formDataToSubmit = { ...formData, query: queryToSave };
        saveSearch(formDataToSubmit);
      },
      text: intl.formatMessage({
        id: 'SaveSearch.saveSearchButton',
        defaultMessage: 'Save Search',
        description: 'Save search button text',
      }),
      tooltip: getDisabledSaveSearchActionButtonTooltip(formData, intl),
    },
    overflowDisplay: 'visible',
    title: intl.formatMessage({
      id: 'SaveSearch.modalTitle',
      defaultMessage: 'Save Search',
      description: 'Save search modal title',
    }),
    content: <SaveSearchForm handleFormDataChanges={setFormData} />,
  });

  return (
    <Layout minW="120" ml={3}>
      <Button
        disabled={!hasSearchQuery}
        text={intl.formatMessage({
          id: 'SaveSearch.buttonText',
          defaultMessage: 'Save Search',
          description: 'Save search button text',
        })}
        tooltip={getSaveSearchIconTooltip()}
        variant="primary"
        onClick={showSaveSearchModal}
      />
      {saveSearchModal}
    </Layout>
  );
};

export default SaveSearch;
